import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { API_V1 } from '../../../core/http/api.config';

export type StatusPortfolio =
  | 'RASCUNHO'
  | 'EM_PRIORIZACAO'
  | 'EM_APROVACAO_EM'
  | 'APROVADO_CHEFE_EM'
  | 'PUBLICADO_BCG'
  | 'ARQUIVADO';

export type Portfolio = {
  id: string;
  anoExecucao: number;
  anoReferenciaConsolidacao: number;
  nome: string;
  status: StatusPortfolio;
  dataLimiteConsolidacao?: string;
  publicadoEm?: string;
  referenciaPublicacaoBcg?: string;
  observacoesGovernanca?: string;
};

export type PortfolioPage = {
  content: Portfolio[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
};

export type PortfolioCreateRequest = {
  anoExecucao: number;
  anoReferenciaConsolidacao: number;
  nome: string;
};

export type PortfolioPatchRequest = {
  nome?: string;
  observacoesGovernanca?: string;
};

export type PublicarBcgRequest = {
  referenciaPublicacaoBcg: string;
  publicadoEm?: string;
};

export type CronogramaMarco = {
  id: string;
  rotulo: string;
  dataSugerida: string;
  dataEfetiva?: string;
  dataCalendario: string;
  artigo: string;
  fundamentacao: string;
  natureza: 'OBRIGATORIO' | 'SUGERIDO';
  faseWorkflow: number;
  desvioPrazo: boolean;
  atualizadoEm?: string;
};

export type CronogramaMarcoUpdateRequest = {
  dataEfetiva: string;
};

export type MetadadosGovernanca = {
  portfolioId: string;
  exercicio: number;
  status: StatusPortfolio;
  geradoEm: string;
  ranking: Array<{
    iniciativaId: string;
    tipo: string;
    scoreConsolidado: number;
    risco?: string;
    prazo?: string;
  }>;
  alertas: Array<{ tipo: string; criticidade: string; mensagem: string }>;
  decisoes: Array<{
    tipoDecisao?: string;
    tipoEvento: string;
    artigoReferencia?: string;
    correlationId?: string;
    ocorridoEm: string;
    detalhe?: string;
  }>;
  marcosCronograma: CronogramaMarco[];
};

@Injectable({ providedIn: 'root' })
export class PortfolioApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${API_V1}/portfolio`;

  listar(page = 0, size = 20, status?: string): Observable<PortfolioPage> {
    let p = new HttpParams().set('page', String(page)).set('size', String(size));
    if (status) {
      p = p.set('status', status);
    }
    return this.http.get<PortfolioPage>(this.base, { params: p });
  }

  obter(portfolioId: string): Observable<Portfolio> {
    return this.http.get<Portfolio>(`${this.base}/${portfolioId}`);
  }

  criar(body: PortfolioCreateRequest): Observable<Portfolio> {
    return this.http.post<Portfolio>(this.base, body);
  }

  atualizar(portfolioId: string, body: PortfolioPatchRequest): Observable<Portfolio> {
    return this.http.patch<Portfolio>(`${this.base}/${portfolioId}`, body);
  }

  submeterPriorizacao(portfolioId: string): Observable<Portfolio> {
    return this.http.post<Portfolio>(`${this.base}/${portfolioId}/submeter-priorizacao`, {});
  }

  submeterAprovacaoEm(portfolioId: string): Observable<Portfolio> {
    return this.http.post<Portfolio>(`${this.base}/${portfolioId}/submeter-aprovacao-em`, {});
  }

  aprovarChefeEm(portfolioId: string): Observable<Portfolio> {
    return this.http.post<Portfolio>(`${this.base}/${portfolioId}/aprovar-chefe-em`, {});
  }

  publicarBcg(portfolioId: string, body: PublicarBcgRequest): Observable<Portfolio> {
    return this.http.post<Portfolio>(`${this.base}/${portfolioId}/publicar-bcg`, body);
  }

  listarMarcosCronograma(portfolioId: string): Observable<CronogramaMarco[]> {
    return this.http.get<CronogramaMarco[]>(`${this.base}/${portfolioId}/cronograma/marcos`);
  }

  atualizarMarcoCronograma(
    portfolioId: string,
    marcoCodigo: string,
    body: CronogramaMarcoUpdateRequest,
  ): Observable<CronogramaMarco> {
    return this.http.patch<CronogramaMarco>(
      `${this.base}/${portfolioId}/cronograma/marcos/${marcoCodigo}`,
      body,
    );
  }

  obterMetadadosGovernanca(portfolioId: string): Observable<MetadadosGovernanca> {
    return this.http.get<MetadadosGovernanca>(`${this.base}/${portfolioId}/metadados-governanca`);
  }
}
